import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Router, Route, Redirect, Switch } from 'react-router-dom';
import Notification from 'react-web-notification';
import { isMobile } from 'react-device-detect';
import {
  Image,
  Dimmer,
  Loader,
  Modal,
  Button,
  Dropdown,
} from 'semantic-ui-react';
import { history } from '../helpers';
import {
  commonActions,
  uiActions,
  userActions,
  workLogActions,
  projectActions,
  notificationActions,
} from './../actions';
import { Dialog } from './views/Dialog';
import Constant from './../common/Constant';
import Strings from './../common/Strings';
import Splash from './Splash';
import Login from './Login';
import Welcome from './Welcome';
import Dashboard from './Dashboard';
import Analytics from '../analytics/Analytics';
import migrateAllData from '../migrations/migration';
import { clientConfig } from '../config';
import './Common.css';
import './App.css';

const firebase = require('firebase/app');
require('firebase/auth');
require('firebase/firestore');

// Snapshot listener remove methods
let userSnapshot;
let historyWorkLogSnapshot;

let acceptInvitationToken;

const moment = require('moment');

class App extends React.Component {
  static stopSync() {
    if (userSnapshot !== undefined) {
      userSnapshot();
    }

    workLogActions.stopSync();
    projectActions.stopSync();

    if (historyWorkLogSnapshot !== undefined) {
      historyWorkLogSnapshot();
    }
  }

  static checkForAcceptInvitation() {
    const { pathname, search } = history.location;

    if (pathname === '/acceptInvitation' && search) {
      const searchParams = new URLSearchParams(search);
      const token = searchParams.get('token');
      const projectName = searchParams.get('projectName');
      const companyName = searchParams.get('companyName');
      if (token && projectName && companyName) {
        acceptInvitationToken = {
          token,
          projectName,
          companyName,
        };
      }
    }
  }

  constructor(props) {
    super(props);

    this.state = {
      invitationProjects: [],
      selectedInvitationProjectId: '',
    };

    App.checkForAcceptInvitation();

    // Listen to history stack to clear alerts when page changes
    const { dispatch } = this.props;
    history.listen(location => {
      App.checkForAcceptInvitation();
      Analytics.pageView(location.pathname + location.search);
      dispatch(uiActions.closeDialog());
    });

    firebase.auth().onAuthStateChanged(user => {
      if (user) {
        // User is signed in.
        dispatch(
          migrateAllData(() => {
            dispatch(userActions.fetchUser());
            dispatch(this.setupUserSync());
            dispatch(workLogActions.fetchTodaysWorkLog());
            dispatch(projectActions.fetchProjects());
          }),
        );
      } else {
        // User is signed out.
        App.stopSync();
        dispatch(notificationActions.cancelAllNotifications());
        dispatch(commonActions.clearStore());
        dispatch(uiActions.initialized(true));
        history.replace('login');
      }
    });
  }

  componentDidMount() {
    document.addEventListener('visibilitychange', this.onVisibilityChange);
  }

  componentWillReceiveProps(nextProps) {
    const { uid, email, displayName } = nextProps.user;
    if (email && displayName && acceptInvitationToken) {
      // Get existings projects which can be linked to invitation
      const promise = projectActions.getProjectsAvailableToLinkForCompany(uid);
      promise
        .then(snapshot => {
          const invitationProjects = [];
          snapshot.forEach(doc => {
            if (!doc.data().companyProjectId) {
              const project = doc.data();
              project.id = doc.id;
              invitationProjects.push(project);
            }
          });
          if (invitationProjects.length > 0) {
            this.setState({ invitationProjects });
          } else {
            this.acceptInvitation();
          }
        })
        .catch(() => {
          this.acceptInvitation();
        });
    }
  }

  componentWillUnmount() {
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
  }

  onVisibilityChange = () => {
    this.checkDay();
  };

  onContinueClicked = () => {
    this.props.dispatch(uiActions.mobileBlockContinueClicked(false));
  };

  setupUserSync = () => dispatch => {
    const { currentUser } = firebase.auth();
    if (currentUser) {
      userSnapshot = firebase
        .firestore()
        .collection('users')
        .doc(currentUser.uid)
        .onSnapshot(snapshot => {
          // When hasPendingWrites is false, it means trigger came from server
          if (!snapshot.metadata.hasPendingWrites) {
            const user = snapshot.data();

            // Check if user data changes
            if (user) {
              dispatch(userActions.settingsUpdated(user));
              dispatch(notificationActions.scheduleNotifications(user));
            }
          }
        });
    }
  };

  checkDay() {
    const { day } = this.props;
    const newDay = moment().format(Constant.DATE_FORMAT_SHORT);

    if (day !== newDay) {
      if (this.props.user.email.length > 0) {
        this.props.dispatch(workLogActions.fetchTodaysWorkLog());
        this.props.dispatch(
          notificationActions.scheduleNotifications(this.props.user),
        );
      }
    }

    this.props.dispatch(uiActions.changeDay(newDay));
  }

  renderDialog() {
    if (this.props.dialog) {
      return (
        <Dialog
          image={this.props.dialog.image}
          title={this.props.dialog.title}
          body={this.props.dialog.body}
          buttonStyle={this.props.dialog.buttonStyle}
          buttonText={this.props.dialog.buttonText}
          buttonOnClick={() => {
            if (this.props.dialog.buttonOnClick) {
              this.props.dialog.buttonOnClick();
            }
            this.props.dispatch(uiActions.closeDialog());
          }}
          onClose={() => this.props.dispatch(uiActions.closeDialog())}
        />
      );
    }

    return null;
  }

  renderNotification() {
    if (this.props.user.settings.alertEnabled) {
      return (
        <Notification
          title={this.props.notificationTitle}
          options={this.props.notificationOptions}
          timeout={60 * 1000}
          onClick={e => {
            e.currentTarget.close();
            window.focus();
            this.props.dispatch(uiActions.menuItemClicked(0));
            history.replace('/activity');
          }}
        />
      );
    }

    return null;
  }

  acceptInvitation = selectedProjectId => {
    const { dispatch } = this.props;
    dispatch(
      userActions.acceptInvitation(acceptInvitationToken, selectedProjectId),
    );
    acceptInvitationToken = null;
    this.setState({ invitationProjects: [], selectedInvitationProjectId: '' });
  };

  renderAcceptInvitationPicker() {
    const { invitationProjects } = this.state;
    if (invitationProjects.length > 0 && acceptInvitationToken) {
      const { selectedInvitationProjectId } = this.state;
      const dropDownText = selectedInvitationProjectId
        ? this.state.invitationProjects.find(
            p => p.id === selectedInvitationProjectId,
          ).name
        : Strings.INVITATION.chooseProject;
      return (
        <Modal
          centered
          open
          size="mini"
          closeIcon
          onClose={() => {
            acceptInvitationToken = null;
            this.setState({
              invitationProjects: [],
              selectedInvitationProjectId: '',
            });
          }}
        >
          <div className="invitation-banner-root-container">
            <div className="invitation-banner-container">
              <Image
                className="invitation-banner-img"
                src="/graphic/invitation_banner.png"
              />
              <Image
                className="invitation-banner-link-img"
                src="/icon/invitation_link.png"
              />
            </div>
            <p className="invitation-banner-project-name">
              {acceptInvitationToken.projectName}
            </p>
            <p className="invitation-banner-project-owner">
              {acceptInvitationToken.companyName}
            </p>
            <Button
              className="common-green-button"
              onClick={() => this.acceptInvitation()}
            >
              {Strings.INVITATION.getStarted}
            </Button>
            <div className="invitation-banner-divider" />
            <p className="invitation-banner-or">{Strings.INVITATION.or}</p>
            <p className="invitation-banner-select-project">
              {Strings.INVITATION.selectProject}
            </p>
            <Dropdown
              text={dropDownText}
              basic
              className="invitation-drop-down"
              fluid
              icon={<div className="invitation-drop-down-icon" />}
            >
              <Dropdown.Menu>
                {this.state.invitationProjects.map(project => (
                  <Dropdown.Item
                    value={project.id}
                    onClick={(e, { value }) => {
                      this.setState({ selectedInvitationProjectId: value });
                    }}
                    className="invitation-drop-down-item"
                  >
                    {project.name}
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>
            <Button
              className="common-green-button"
              disabled={!this.state.selectedInvitationProjectId}
              onClick={() =>
                this.acceptInvitation(this.state.selectedInvitationProjectId)
              }
            >
              {Strings.INVITATION.merge}
            </Button>
          </div>
        </Modal>
      );
    }

    return null;
  }

  renderAcceptingInvitation() {
    if (this.props.isAcceptingInvitation) {
      return (
        <Dimmer active>
          <Loader>{Strings.COMMON.acceptingInvitation}</Loader>
        </Dimmer>
      );
    }

    return null;
  }

  renderMobileBlock() {
    return (
      <div className="mobile-block-container">
        <div className="space" />
        <Image
          id="app-logo"
          className="mobile-block-top-img"
          src="/logo-app.png"
        />
        <p id="mobile-block-header">{Strings.MOBILE_BLOCK.header}</p>
        <p id="mobile-block-get-app">{Strings.MOBILE_BLOCK.getApp}</p>
        <div className="mobile-block-download-container">
          <a href="https://itunes.apple.com/us/app/tictok-timesheet-tamer/id1360997148?ls=1&mt=8">
            <Image
              id="mobile-block-download-app-store"
              src="https://timetracker-48cd0.firebaseapp.com/images/download_app_store.png"
            />
          </a>
          <a href="https://play.google.com/store/apps/details?id=fi.finlabs.timetracker">
            <Image
              id="mobile-block-download-google-play"
              src="https://timetracker-48cd0.firebaseapp.com/images/download_google_play.png"
            />
          </a>
        </div>
        <p id="mobile-block-body">{Strings.MOBILE_BLOCK.body}</p>
        <p id="mobile-block-continue" onClick={this.onContinueClicked}>
          {Strings.MOBILE_BLOCK.continue}
        </p>
        <div className="space" />
      </div>
    );
  }

  render() {
    if (isMobile && this.props.showMobileBlock) {
      return <div>{this.renderMobileBlock()}</div>;
    }

    if (!this.props.isInitialized) {
      return (
        <div className="common-app-root">
          <Router history={history}>
            <Switch>
              <Route path="/" component={Splash} />
              <Route render={() => <Redirect to="/" />} />
            </Switch>
          </Router>
        </div>
      );
    }

    const { email, displayName } = this.props.user;
    if (email.length > 0) {
      if (displayName && displayName.length > 0) {
        return (
          <div className="common-app-root">
            {this.renderDialog()}
            {this.renderNotification()}
            {this.renderAcceptInvitationPicker()}
            {this.renderAcceptingInvitation()}
            <Router history={history}>
              <Switch>
                <Route path="/" component={Dashboard} />
                <Route render={() => <Redirect to="/" />} />
              </Switch>
            </Router>
          </div>
        );
      }
      return (
        <div className="common-app-root">
          {this.renderDialog()}
          <Router history={history}>
            <Switch>
              <Route path="/welcome" component={Welcome} />
              <Route render={() => <Redirect to="/welcome" />} />
            </Switch>
          </Router>
        </div>
      );
    }
    return (
      <div className="common-app-root">
        {this.renderDialog()}
        <Router history={history}>
          <Switch>
            <Route path="/login" component={Login} />
            <Route path="/signup" component={Login} />
            <Route render={() => <Redirect to="/login" />} />
          </Switch>
        </Router>
      </div>
    );
  }
}

App.propTypes = {
  dispatch: PropTypes.func.isRequired,
  day: PropTypes.string.isRequired,
  showMobileBlock: PropTypes.bool.isRequired,
  isInitialized: PropTypes.bool.isRequired,
  user: PropTypes.shape({
    uid: PropTypes.string.isRequired,
    email: PropTypes.string.isRequired,
    displayName: PropTypes.string.isRequired,
    settings: PropTypes.shape({
      alertEnabled: PropTypes.bool.isRequired,
    }).isRequired,
  }).isRequired,
  dialog: PropTypes.shape({
    image: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    body: PropTypes.string.isRequired,
    buttonStyle: PropTypes.string.isRequired,
    buttonText: PropTypes.string.isRequired,
    buttonOnClick: PropTypes.string.isRequired,
  }).isRequired,
  notificationTitle: PropTypes.string.isRequired,
  notificationOptions: PropTypes.shape().isRequired,
  isAcceptingInvitation: PropTypes.bool.isRequired,
};

const mapStateToProps = state => ({
  day: state.uiState.day,
  showMobileBlock: state.uiState.showMobileBlock,
  isInitialized: state.uiState.isInitialized,
  user: state.user,
  dialog: state.uiState.dialog,
  notificationTitle: state.uiState.notificationTitle,
  notificationOptions: state.uiState.notificationOptions,
  isAcceptingInvitation: state.uiState.isAcceptingInvitation,
});

export default connect(mapStateToProps)(App);



// WEBPACK FOOTER //
// ./src/components/App.js